/**
 * Helpers para exportar el detalle de un cierre de caja a CSV. El shape
 * del input viene de `cash.sessionDetail` en el worker.
 *
 * El CSV incluye varias "secciones" separadas por filas en blanco:
 *   - Header (sesión, cajero, fechas).
 *   - Resumen de efectivo (apertura, esperado, cierre, diferencia).
 *   - Ventas por método de pago.
 *   - Movimientos del turno.
 *   - Pedidos del turno.
 *
 * No usamos una lib como `papaparse` — las reglas CSV (escapar `"` y
 * envolver celdas con comas o saltos de línea) caben en 10 líneas.
 */

import { formatCOP } from './format.js';
import { orderOriginLabels, orderStatusLabels } from './orders.js';
import { paymentMethodLabels } from './payments.js';

export { paymentMethodLabels };

import type { OrderOrigin, OrderStatus } from '@atreus/core';

export interface CashSessionDetail {
  session: {
    id: string;
    userId: string;
    userEmail: string | null;
    userFullName: string | null;
    openedAt: number;
    closedAt: number | null;
    openingCashCents: number;
    closingCashCents: number | null;
    expectedCashCents: number;
    note: string | null;
  };
  salesByMethod: Record<string, number>;
  totalSalesCents: number;
  /** Fiado otorgado en el turno (venta, no dinero). Worker ≥ 0011. */
  creditCents?: number;
  collectionsByMethod?: Record<string, number>;
  collectionsTotalCents?: number;
  /** Ventas no-fiado + abonos a cartera. */
  receivedTotalCents?: number;
  cashFlow: {
    cashSalesCents: number;
    cashCollectionsCents?: number;
    cashInCents: number;
    cashOutCents: number;
    dropsCents: number;
  };
  /** Abonos a cartera cobrados en el turno. */
  collections?: Array<{
    id: string;
    customerName: string | null;
    method: string;
    amountCents: number;
    receivedAt: number;
    note: string | null;
  }>;
  movements: Array<{
    id: string;
    type: string;
    amountCents: number;
    reason: string | null;
    at: number;
  }>;
  orders: Array<{
    id: string;
    origin: OrderOrigin;
    tableLabel: string | null;
    tableNumber: number | null;
    status: OrderStatus;
    totalCents: number;
    subtotalCents: number;
    taxCents: number;
    tipCents: number;
    discountCents: number;
    openedAt: number;
    closedAt: number | null;
    payments: Array<{ method: string; amountCents: number }>;
  }>;
}

export const movementTypeLabels: Record<string, string> = {
  in: 'Entrada',
  out: 'Salida',
  drop: 'Drop (a bóveda)',
  sale: 'Cobro en efectivo',
  refund: 'Devolución (pago anulado)',
};

const fmtDate = (ms: number | null): string =>
  ms == null
    ? ''
    : new Date(ms).toLocaleString('es-CO', {
        dateStyle: 'short',
        timeStyle: 'medium',
      });

const fmtCents = (cents: number): string =>
  // Quoteamos para que Excel no parsee "$1.500" como número extraño.
  `"${formatCOP(cents)}"`;

const escapeCell = (val: string | number | null): string => {
  if (val == null) return '';
  const s = String(val);
  if (s.includes('"') || s.includes(',') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

export const buildCashReportCsv = (d: CashSessionDetail): string => {
  const lines: string[] = [];
  const push = (cells: Array<string | number | null>): void => {
    lines.push(cells.map(escapeCell).join(','));
  };

  // --- Header ---
  push(['Cierre de caja']);
  push(['Sesión', d.session.id]);
  push([
    'Cajero',
    d.session.userFullName ?? d.session.userEmail ?? d.session.userId,
  ]);
  push(['Abierto', fmtDate(d.session.openedAt)]);
  push(['Cerrado', fmtDate(d.session.closedAt)]);
  if (d.session.note) push(['Nota', d.session.note]);
  push([]);

  // --- Resumen de efectivo ---
  push(['Resumen de efectivo']);
  push(['Apertura', fmtCents(d.session.openingCashCents)]);
  push(['Ventas en efectivo', fmtCents(d.cashFlow.cashSalesCents)]);
  if ((d.cashFlow.cashCollectionsCents ?? 0) > 0) {
    push([
      'Abonos a cartera en efectivo',
      fmtCents(d.cashFlow.cashCollectionsCents ?? 0),
    ]);
  }
  push(['Entradas manuales', fmtCents(d.cashFlow.cashInCents)]);
  push(['Salidas manuales', fmtCents(d.cashFlow.cashOutCents)]);
  push(['Drops a bóveda', fmtCents(d.cashFlow.dropsCents)]);
  push(['Efectivo esperado', fmtCents(d.session.expectedCashCents)]);
  if (d.session.closingCashCents != null) {
    const diff = d.session.closingCashCents - d.session.expectedCashCents;
    push(['Efectivo contado', fmtCents(d.session.closingCashCents)]);
    push(['Diferencia', fmtCents(diff)]);
  }
  push([]);

  // --- Ventas por método ---
  push(['Ventas por método de pago']);
  push(['Método', 'Total']);
  for (const [method, total] of Object.entries(d.salesByMethod)) {
    push([paymentMethodLabels[method] ?? method, fmtCents(total)]);
  }
  push(['Total ventas', fmtCents(d.totalSalesCents)]);
  if (
    d.receivedTotalCents != null &&
    ((d.creditCents ?? 0) > 0 || (d.collectionsTotalCents ?? 0) > 0)
  ) {
    push(['Total recibido (sin crédito, con abonos)', fmtCents(d.receivedTotalCents)]);
  }
  push([]);

  // --- Abonos a cartera ---
  if (d.collections && d.collections.length > 0) {
    push(['Abonos a cartera']);
    push(['Fecha/hora', 'Cliente', 'Método', 'Monto', 'Nota']);
    for (const c of d.collections) {
      push([
        fmtDate(c.receivedAt),
        c.customerName ?? '',
        paymentMethodLabels[c.method] ?? c.method,
        fmtCents(c.amountCents),
        c.note ?? '',
      ]);
    }
    push(['Total abonos', fmtCents(d.collectionsTotalCents ?? 0)]);
    push([]);
  }

  // --- Movimientos ---
  push(['Movimientos del turno']);
  if (d.movements.length === 0) {
    push(['(sin movimientos)']);
  } else {
    push(['Fecha/hora', 'Tipo', 'Monto', 'Motivo']);
    for (const m of d.movements) {
      push([
        fmtDate(m.at),
        movementTypeLabels[m.type] ?? m.type,
        fmtCents(m.amountCents),
        m.reason ?? '',
      ]);
    }
  }
  push([]);

  // --- Pedidos ---
  push(['Pedidos del turno']);
  if (d.orders.length === 0) {
    push(['(sin pedidos)']);
  } else {
    push([
      'Pedido',
      'Origen',
      'Mesa',
      'Estado',
      'Total productos',
      'IVA',
      'Propina',
      'Descuento',
      'Total',
      'Métodos de pago',
      'Cerrado',
    ]);
    for (const o of d.orders) {
      const tableLabel =
        o.origin === 'table'
          ? (o.tableLabel ??
            (o.tableNumber != null ? String(o.tableNumber) : ''))
          : '';
      const methods = o.payments
        .map(
          (p) =>
            `${paymentMethodLabels[p.method] ?? p.method} ${formatCOP(p.amountCents)}`,
        )
        .join(' + ');
      push([
        `#${o.id.slice(-6).toUpperCase()}`,
        orderOriginLabels[o.origin],
        tableLabel,
        orderStatusLabels[o.status],
        fmtCents(o.subtotalCents),
        fmtCents(o.taxCents),
        fmtCents(o.tipCents),
        fmtCents(o.discountCents),
        fmtCents(o.totalCents),
        methods,
        fmtDate(o.closedAt),
      ]);
    }
  }

  // BOM para que Excel abra UTF-8 sin romper acentos.
  return '\uFEFF' + lines.join('\r\n');
};

export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const cashReportFilename = (d: CashSessionDetail): string => {
  const dt = new Date(d.session.openedAt)
    .toISOString()
    .slice(0, 10)
    .replace(/-/g, '');
  const who = (
    d.session.userFullName ??
    d.session.userEmail ??
    d.session.userId
  )
    .replace(/[^a-z0-9-_]+/gi, '-')
    .slice(0, 40);
  return `cierre-${dt}-${who}-${d.session.id.slice(-6)}.csv`;
};
